/**
 * Componente de Tabela de Itens da DLQ
 * 
 * Exibe tabela com itens da DLQ, permitindo reprocessar ou descartar
 * individualmente ou em lote.
 * 
 * @author Sistema de Flows v3.0 - DLQ
 */

import { useState } from 'react';
import { RefreshCw, Trash2, Eye, ChevronDown, ChevronUp, CheckSquare, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useDlqItems, useReprocessDlqItem, useDiscardDlqItem, useReprocessDlqBatch, DlqItem } from '@/hooks/useDlqMonitor';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Labels legíveis para cada tipo de reason
 */
const reasonLabels: Record<string, string> = {
  'max_attempts_exceeded': 'Max Tentativas',
  'max_node_retries_exceeded': 'Max Retries Nó',
  'flow_engine_error': 'Erro Flow Engine',
  'timeout': 'Timeout',
  'invalid_payload': 'Payload Inválido',
};

/**
 * Cores para badges de reason
 */
const reasonVariants: Record<string, 'default' | 'destructive' | 'outline' | 'secondary'> = {
  'max_attempts_exceeded': 'default',
  'max_node_retries_exceeded': 'secondary',
  'flow_engine_error': 'destructive',
  'timeout': 'outline',
  'invalid_payload': 'outline',
};

interface DlqItemRowProps {
  item: DlqItem;
  isSelected: boolean;
  onSelect: (selected: boolean) => void;
  onReprocess: () => void;
  onDiscard: () => void;
  isReprocessing: boolean;
  isDiscarding: boolean;
}

function DlqItemRow({ 
  item, 
  isSelected, 
  onSelect, 
  onReprocess, 
  onDiscard,
  isReprocessing,
  isDiscarding 
}: DlqItemRowProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <TableRow className={isSelected ? 'bg-muted/50' : ''}>
        <TableCell>
          <button 
            onClick={() => onSelect(!isSelected)}
            className="p-1 hover:bg-muted rounded"
          >
            {isSelected ? (
              <CheckSquare className="h-4 w-4 text-primary" />
            ) : (
              <Square className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        </TableCell>
        <TableCell className="font-mono text-xs">
          {item.msg_id}
        </TableCell>
        <TableCell className="text-xs">
          {format(new Date(item.enqueued_at), 'dd/MM HH:mm:ss', { locale: ptBR })}
        </TableCell>
        <TableCell>
          <Badge variant={reasonVariants[item.dlq_reason] || 'outline'}>
            {reasonLabels[item.dlq_reason] || item.dlq_reason}
          </Badge>
        </TableCell>
        <TableCell className="text-sm">
          {item.tenant_name || 'N/A'}
        </TableCell>
        <TableCell className="text-sm">
          {item.flow_name || 'N/A'}
        </TableCell>
        <TableCell className="text-sm font-mono">
          {item.contact_phone || '-'}
        </TableCell>
        <TableCell className="text-sm">
          {item.action || '-'}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="icon" className="h-7 w-7">
                {isOpen ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7 text-green-600 hover:text-green-700"
              onClick={onReprocess}
              disabled={isReprocessing}
              title="Reprocessar"
            >
              <RefreshCw className={`h-4 w-4 ${isReprocessing ? 'animate-spin' : ''}`} />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-7 w-7 text-destructive hover:text-destructive/80"
                  disabled={isDiscarding}
                  title="Descartar"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Descartar item?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta ação irá arquivar permanentemente o item da DLQ. 
                    Ele não será reprocessado e ficará apenas para auditoria.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={onDiscard}>
                    Descartar
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </TableCell>
      </TableRow>
      <CollapsibleContent asChild>
        <TableRow>
          <TableCell colSpan={10} className="bg-muted/30">
            <div className="p-4 space-y-3">
              {item.dlq_error && (
                <div>
                  <span className="text-sm font-medium text-destructive">Erro:</span>
                  <p className="text-sm text-muted-foreground mt-1">{item.dlq_error}</p>
                </div>
              )}
              <div>
                <span className="text-sm font-medium">Payload:</span>
                <ScrollArea className="h-[150px] mt-1">
                  <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
                    {JSON.stringify(item.message, null, 2)}
                  </pre>
                </ScrollArea>
              </div>
            </div>
          </TableCell>
        </TableRow>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function DlqItemsTable() {
  const [reasonFilter, setReasonFilter] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  
  const { data: items, isLoading } = useDlqItems(100, 0, reasonFilter);
  const reprocessMutation = useReprocessDlqItem();
  const discardMutation = useDiscardDlqItem();
  const reprocessBatchMutation = useReprocessDlqBatch();

  // Handlers
  const handleReprocess = async (msgId: number) => {
    try {
      await reprocessMutation.mutateAsync(msgId);
      toast.success('Item reenfileirado para processamento');
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(msgId);
        return next;
      });
    } catch (error) {
      toast.error('Erro ao reprocessar item');
    }
  };

  const handleDiscard = async (msgId: number) => {
    try {
      await discardMutation.mutateAsync(msgId);
      toast.success('Item descartado');
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(msgId);
        return next;
      });
    } catch (error) {
      toast.error('Erro ao descartar item');
    }
  };

  const handleReprocessSelected = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      const count = await reprocessBatchMutation.mutateAsync(Array.from(selectedIds));
      toast.success(`${count} itens reenfileirados`);
      setSelectedIds(new Set());
    } catch (error) {
      toast.error('Erro ao reprocessar itens');
    }
  };

  const handleSelectAll = () => {
    if (!items) return;
    
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(i => i.msg_id)));
    }
  };

  const handleSelectItem = (msgId: number, selected: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(msgId);
      } else {
        next.delete(msgId);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-40" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg">Itens na DLQ</CardTitle>
            <CardDescription>
              {items?.length || 0} itens aguardando ação
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select 
              value={reasonFilter || 'all'} 
              onValueChange={(v) => setReasonFilter(v === 'all' ? null : v)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtrar por motivo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os motivos</SelectItem>
                <SelectItem value="max_attempts_exceeded">Max Tentativas</SelectItem>
                <SelectItem value="max_node_retries_exceeded">Max Retries Nó</SelectItem>
                <SelectItem value="flow_engine_error">Erro Flow Engine</SelectItem>
                <SelectItem value="timeout">Timeout</SelectItem>
                <SelectItem value="invalid_payload">Payload Inválido</SelectItem>
              </SelectContent>
            </Select>
            {selectedIds.size > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleReprocessSelected}
                disabled={reprocessBatchMutation.isPending}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${reprocessBatchMutation.isPending ? 'animate-spin' : ''}`} />
                Reprocessar ({selectedIds.size})
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!items || items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Nenhum item na DLQ
          </div>
        ) : (
          <div className="border rounded-md">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <button 
                      onClick={handleSelectAll}
                      className="p-1 hover:bg-muted rounded"
                    >
                      {selectedIds.size === items.length ? (
                        <CheckSquare className="h-4 w-4 text-primary" />
                      ) : (
                        <Square className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>
                  </TableHead>
                  <TableHead className="w-16">ID</TableHead>
                  <TableHead className="w-28">Data</TableHead>
                  <TableHead className="w-32">Motivo</TableHead>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Flow</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Ação</TableHead>
                  <TableHead className="w-28">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <DlqItemRow
                    key={item.msg_id}
                    item={item}
                    isSelected={selectedIds.has(item.msg_id)}
                    onSelect={(selected) => handleSelectItem(item.msg_id, selected)}
                    onReprocess={() => handleReprocess(item.msg_id)}
                    onDiscard={() => handleDiscard(item.msg_id)}
                    isReprocessing={reprocessMutation.isPending}
                    isDiscarding={discardMutation.isPending}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
